import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export interface Comision {
  id?: string;
  sellerId: string;
  sellerName: string;
  lotId: string;
  lotNumber: string;
  buyerName: string;
  saleDate: string;
  totalValue: number;
  commissionAmount: number;
  commissionPercentage: number;
  paid: boolean;
  paymentDate?: string;
  transferencia?: string;
  recibo?: string;
  createdAt: string;
}

export function useComisiones() {
  const [comisiones, setComisiones] = useState<Comision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Suscripción en tiempo real a la colección 'comisiones'
  useEffect(() => {
    setLoading(true);
    const unsubscribe = onSnapshot(collection(db, 'comisiones'), (snapshot) => {
      const comisionesData: Comision[] = [];
      snapshot.forEach((doc) => {
        comisionesData.push({ id: doc.id, ...doc.data() } as Comision);
      });
      setComisiones(comisionesData);
      setLoading(false);
    }, (err) => {
      console.error('Error listening to comisiones:', err);
      setError('Error al sincronizar comisiones');
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const fetchComisiones = async () => {
    try {
      setLoading(true);
      console.log('Firestore projectId:', db.app.options.projectId); // Log para depuración
      const querySnapshot = await getDocs(collection(db, 'comisiones'));
      const comisionesData: Comision[] = [];
      querySnapshot.forEach((doc) => {
        comisionesData.push({ id: doc.id, ...doc.data() } as Comision);
      });
      console.log('Comisiones traídas de Firestore:', comisionesData); // Log para depuración
      setComisiones(comisionesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching comisiones:', err);
      setError('Error al cargar comisiones');
    } finally {
      setLoading(false);
    }
  };

  const addComision = async (comision: Omit<Comision, 'id' | 'createdAt'>, customId?: string) => {
    try {
      const comisionData = {
        ...comision,
        createdAt: new Date().toISOString()
      };
      if (customId) {
        await setDoc(doc(db, 'comisiones', customId), comisionData);
        const newComision = { id: customId, ...comisionData };
        setComisiones(prev => [...prev, newComision]);
        return customId;
      } else {
        const docRef = await addDoc(collection(db, 'comisiones'), comisionData);
        const newComision = { id: docRef.id, ...comisionData };
        setComisiones(prev => [...prev, newComision]);
        return docRef.id;
      }
    } catch (err) {
      console.error('Error adding comision:', err);
      setError('Error al agregar comisión');
      throw err;
    }
  };

  const updateComision = async (id: string, updates: Partial<Comision>) => {
    try {
      await updateDoc(doc(db, 'comisiones', id), updates);
      setComisiones(prev => prev.map(comision => 
        comision.id === id ? { ...comision, ...updates } : comision
      ));
    } catch (err) {
      console.error('Error updating comision:', err);
      setError('Error al actualizar comisión');
      throw err;
    }
  };

  const deleteComision = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'comisiones', id));
      setComisiones(prev => prev.filter(comision => comision.id !== id));
    } catch (err) {
      console.error('Error deleting comision:', err);
      setError('Error al eliminar comisión');
      throw err;
    }
  };

  const getComisionesBySeller = async (sellerId: string) => {
    try {
      const q = query(collection(db, 'comisiones'), where('sellerId', '==', sellerId));
      const querySnapshot = await getDocs(q);
      const comisionesData: Comision[] = [];
      querySnapshot.forEach((doc) => {
        comisionesData.push({ id: doc.id, ...doc.data() } as Comision);
      });
      return comisionesData;
    } catch (err) {
      console.error('Error fetching comisiones by seller:', err);
      throw err;
    }
  };

  const markComisionAsPaid = async (id: string, paymentDate?: string) => {
    try {
      const updates = {
        paid: true,
        paymentDate: paymentDate || new Date().toISOString()
      };
      await updateComision(id, updates);
    } catch (err) {
      console.error('Error marking comision as paid:', err);
      throw err;
    }
  };

  return {
    comisiones,
    loading,
    error,
    fetchComisiones,
    addComision,
    updateComision,
    deleteComision,
    getComisionesBySeller,
    markComisionAsPaid
  };
} 